import { handleError, hasError, tryCatch, unknownError } from '@repo/shared/api/helpers'
import { AdminResourcesRoutes } from '@repo/shared/api-routes/admin/resources'
import { CaseWorkerIdFilter_AllBeneficiaries } from '@repo/shared/dtos/caseWorker'
import {
  OutputTypeForRESOURCES_FILE_LOAD,
  PromptTemplateType,
} from '@repo/shared/dtos/organization'
import { BeneficiariesRoutes } from '@repo/shared/routes-beneficiaries'
import { CaseWorkersRoutes } from '@repo/shared/routes-case-workers'
import { FilesRoutes } from '@repo/shared/routes-files'
import { PromptTemplatesRoutes } from '@repo/shared/routes-prompts'
import { AxiosInstance } from 'axios'

import { apiCaseWorkers } from '@/external/api/chunks/api-case-workers'
import { apiOrganizations } from '@/external/api/chunks/api-organizations'
import {
  buildBeneficiaryFromDto,
  buildCaseWorkerFromDto,
  buildPromptTemplateFromDto,
  buildResourceFromDto,
} from '@/external/api/helpers'
import { mockedData } from '@/store/mock/mock'
import { IApi } from '@/store/spi/api'

export const apiAdmin: (api?: AxiosInstance) => IApi['admin'] = (api) => ({
  getInitialValues: api
    ? async ({ organizationId }) => {
        const beneficiariesRes = await tryCatch(
          async () =>
            await api.get<BeneficiariesRoutes.get.Response>(
              BeneficiariesRoutes.get.call({
                caseWorkerId: CaseWorkerIdFilter_AllBeneficiaries,
                organizationId,
              }),
            ),
        )
        if (hasError(beneficiariesRes)) return handleError(beneficiariesRes)

        const caseWorkersRes = await tryCatch(
          async () =>
            await api.get<CaseWorkersRoutes.get.Response>(
              CaseWorkersRoutes.get.call({ organizationId }),
            ),
        )
        if (hasError(caseWorkersRes)) return handleError(caseWorkersRes)

        // FIXME:
        const resourcesRes = (
          await api.get<typeof AdminResourcesRoutes.getAll.response>(
            AdminResourcesRoutes.getAll.getPath({ organizationId }),
          )
        ).data
        if (hasError(resourcesRes)) return handleError(resourcesRes)

        return {
          beneficiaries: beneficiariesRes.beneficiaries.map(buildBeneficiaryFromDto),
          caseWorkers: caseWorkersRes.caseWorkers.map(buildCaseWorkerFromDto),
          resources: resourcesRes.data.map(buildResourceFromDto),
        }
      }
    : () => {
        const beneficiaries = mockedData.beneficiaries
        const caseWorkers = mockedData.caseWorkers
        return Promise.resolve({
          beneficiaries,
          caseWorkers,
          resources: mockedData.resources,
          promptTemplates: mockedData.promptTemplates,
        })
      },

  caseWorkers: apiCaseWorkers(api).admin,
  organizations: apiOrganizations(api).admin,

  prompts: {
    templates: {
      get: api
        ? async ({ organizationId }) => {
            const res = await tryCatch(
              async () =>
                await api.get<PromptTemplatesRoutes.get.Response>(
                  PromptTemplatesRoutes.get.call({
                    organizationId,
                  }),
                ),
            )
            if (hasError(res)) return handleError(res)
            return res.map((template) =>
              buildPromptTemplateFromDto(template.template, template.keywords),
            )
          }
        : () => {
            return Promise.resolve(mockedData.promptTemplates)
          },

      getRevisions: api
        ? async ({ organizationId, promptTemplateType }) => {
            const res = await tryCatch(
              async () =>
                await api.get<PromptTemplatesRoutes.getRevisions.Response>(
                  PromptTemplatesRoutes.getRevisions.call({
                    organizationId,
                    promptTemplateType,
                  }),
                ),
            )
            if (hasError(res)) return handleError(res)
            return res.promptTemplates.map((template) => buildPromptTemplateFromDto(template, []))
          }
        : (p) => {
            const promptTemplates = mockedData.promptTemplates.filter(
              (pt) => pt.promptTemplateType === p.promptTemplateType,
            )
            return Promise.resolve(promptTemplates)
          },

      patchOne: api
        ? async ({ organizationId, promptTemplateId, promptTemplateType, fields }) => {
            const templateRes = await tryCatch(
              async () =>
                await api.patch<PromptTemplatesRoutes.patchOne.Response>(
                  PromptTemplatesRoutes.patchOne.call({
                    organizationId,
                    promptTemplateId,
                    promptTemplateType,
                  }),
                  { fields } satisfies PromptTemplatesRoutes.patchOne.Request,
                ),
            )
            if (hasError(templateRes)) return handleError(templateRes)

            const keywordsRes = await tryCatch(
              async () =>
                await api.get<PromptTemplatesRoutes.getKeywords.Response>(
                  PromptTemplatesRoutes.getKeywords.call({
                    organizationId,
                    promptTemplateType,
                  }),
                ),
            )
            if (hasError(keywordsRes)) return handleError(keywordsRes)

            return buildPromptTemplateFromDto(templateRes.promptTemplate, keywordsRes.keywords)
          }
        : (p) => {
            if (p.fields.promptText) {
              const promptTemplate = mockedData.promptTemplates[0]
              if (promptTemplate) {
                return Promise.resolve({
                  ...promptTemplate,
                  promptText: p.fields.promptText,
                })
              }
            }
            return Promise.resolve(unknownError)
          },
    },
  },

  resources: {
    processResource: api
      ? async ({ organizationId, file }) => {
          const formData = new FormData()
          formData.append('file', file)
          const res = await tryCatch(
            async () =>
              await api.post<FilesRoutes.processFile.Response>(
                FilesRoutes.processFile.call({
                  organizationId,
                  processFilePromptTemplateType: PromptTemplateType.RESOURCES_FILE_LOAD,
                }),
                formData,
                {
                  headers: {
                    'Content-Type': 'multipart/form-data',
                  },
                },
              ),
          )
          if (hasError(res)) return handleError(res)
          return res.promptResult as OutputTypeForRESOURCES_FILE_LOAD
        }
      : () => {
          return Promise.resolve({
            lang: 'eng',
            title: 'resource title ',
            summary: 'resource content',
            traceUrl: '',
          } as OutputTypeForRESOURCES_FILE_LOAD)
        },
  },
})
